import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { isCategoriesWithoutItems } from '../../helpers/menuGroupEdit';
import menuGroupEditStore from '../../store/menuGroupEditStore';
import MenuGroupCategories from '../MenuGroupCategories/MenuGroupCategories';
import { DEBOUNCE_TIME } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { emptyArray } from 'src/utils/helpers';
import AddButton from 'src/view/components/Controls/AddButton/AddButton';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import SearchInput from 'src/view/components/Controls/SearchInput/SearchInput';
import {
  CATEGORY_MODAL_CATEGORY,
  CATEGORY_MODAL_PRODUCT,
  CATEGORY_MODAL_SECTIONS,
  ICategoryOrProductItem,
} from 'src/view/containers/Modals/CategoryAndProductModal/CategoryAndProductModal';
import { IMenuGroupCategory, IMenuGroupProduct } from '~/view/screens/MenuManagementScreen/screens/MenuGroupsScreen/types/menuGroups';
import './style.scss';
import { PRODUCT_TYPE } from '~/types/product';

const MenuGroupEditHeader = observer((): JSX.Element => {
  const { categories } = menuGroupEditStore.editableMenuGroup;
  const { isFocused: isSearchActive } = menuGroupEditStore.filters.search;

  const showSearch = useMemo(
    () => (categories.length && !isCategoriesWithoutItems(categories)) || menuGroupEditStore.filters.search.isFocused,
    [categories]
  );

  const changeMenuGroupCategories = (_categories: ICategoryOrProductItem[] | undefined) => {
    menuGroupEditStore.changeMenuGroupCategories(_categories as IMenuGroupCategory[]);
    modalStore.closeCategoryAndProductModal();
  };

  const changeMenuGroupCategoryItems = (_categories: ICategoryOrProductItem[] | undefined, items: ICategoryOrProductItem[] | undefined) => {
    menuGroupEditStore.changeMenuGroupCategoryItems(items as IMenuGroupProduct[]);
    modalStore.closeCategoryAndProductModal();
  };

  const openCategoriesModal = () => {
    if (disabled) return;

    modalStore.openCategoryAndProductModal({
      selectedCategories: menuGroupEditStore.editableMenuGroup.categories,
      isCategories: CATEGORY_MODAL_CATEGORY.MULTIPLE,
      isProducts: CATEGORY_MODAL_PRODUCT.NONE,
      defaultSection: CATEGORY_MODAL_SECTIONS.CATEGORIES,
      headerTitle: 'add_category_popup',
      isAllCategories: false,
      isAllStatuses: true,
      categoriesWithProducts: true,
      onSave: changeMenuGroupCategories,
    });
  };

  const openItemsModal = () => {
    if (!menuGroupEditStore.selectedCategory || disabled) return;

    modalStore.openCategoryAndProductModal({
      selectedCategories: [menuGroupEditStore.selectedCategory],
      selectedProducts: menuGroupEditStore.selectedCategory.items || [],
      isCategories: CATEGORY_MODAL_CATEGORY.NONE,
      productTypes: [PRODUCT_TYPE.PRODUCT, PRODUCT_TYPE.MEAL_COMBO, PRODUCT_TYPE.DEAL_COMBO],
      isProducts: CATEGORY_MODAL_PRODUCT.MULTIPLE,
      defaultSection: CATEGORY_MODAL_SECTIONS.ITEMS,
      headerTitle: localeStore.t('add_items_from_category', { selected_category: menuGroupEditStore.selectedCategory.name[localeStore.language] }),
      isAllCategories: false,
      isAllStatuses: true,
      onSave: changeMenuGroupCategoryItems,
    });
  };

  const setSearchValue = (value: string) => {
    menuGroupEditStore.setFilters({ search: { ...menuGroupEditStore.filters.search, value: value.toLowerCase() } });
  };

  const setSearchFocus = (isFocused: boolean) => {
    menuGroupEditStore.setFilters({ search: { ...menuGroupEditStore.filters.search, isFocused } });
  };

  const disabled: boolean = menuGroupEditStore.loadingOnFetch || menuGroupEditStore.loadingOnSave;

  if (menuGroupEditStore.loadingOnFetch && !categories.length) {
    return (
      <div className="d-flex flex-column">
        <Skeleton width={360} height={46} style={{ borderRadius: 12 }} className="mb-4" />
        <div className="d-flex flex-row gap-10">
          {emptyArray().map((_, index) => (
            <Skeleton key={'key-' + String(index)} width={250} height={60} style={{ borderRadius: 16 }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-row justify-content-between mr-4">
        <div className="d-flex flex-row">
          {showSearch && (
            <SearchInput
              className="menu-group-edit-header__search mr-2"
              inputClassName="menu-group-edit-header__search-input"
              value={menuGroupEditStore.filters.search.value}
              placeholder="txt_search"
              customPlaceholder
              withResetIcon={isSearchActive}
              resetIconWithEmptyValue
              debounceDelay={DEBOUNCE_TIME.MEDIUM}
              onChangeText={setSearchValue}
              onFocus={() => setSearchFocus(true)}
              onReset={() => setSearchFocus(false)}
            />
          )}
          {!isSearchActive && (
            <MultiLanguageInput
              title="menu_group_name"
              enText={menuGroupEditStore.editableMenuGroup.name.en}
              arText={menuGroupEditStore.editableMenuGroup.name.ar}
              onChangeValue={menuGroupEditStore.changeMenuGroupName}
              validation={toJS(menuGroupEditStore.validation.name)}
              disabled={disabled}
            />
          )}
        </div>
        {!isSearchActive && <Button title={localeStore.t('add_category')} onClick={openCategoriesModal} disabled={disabled} appearance="secondary" />}
      </div>
      {!isSearchActive && (
        <>
          {categories.length ? <MenuGroupCategories categories={categories} /> : null}
          <AddButton title="add_item" className={!categories.length || disabled ? 'disabled mt-4' : 'mt-2'} onAdd={openItemsModal} />
        </>
      )}
      {!categories.length && !menuGroupEditStore.loadingOnFetch ? (
        <p className={`text-size-14 ${menuGroupEditStore.validation.categories ? 'color-gray2 family-medium' : 'color-red family-regular'} mt-4`}>
          {localeStore.t(menuGroupEditStore.validation.categories ? 'mg_add_categories_first' : 'please_add_category_item')}
        </p>
      ) : null}
    </>
  );
});

export default MenuGroupEditHeader;
